import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Badge,
  Popover,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Call as CallIconIx } from "iconsax-react";
import { toast } from "react-hot-toast";
import connectionMonitor from "../../../utils/connectionMonitor";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../../utils/tooltipStyles";
import IconButtonWithTooltip from "../../../components/Inbox/Shared/IconButtonWithTooltip";
import useCallPermission from "../hooks/useCallPermission";
import BetaTag from "./BetaTag";

const STATUS_META = {
  permanent: { color: "#1f7a6c", label: "Permanent permission" },
  temporary: { color: "#b8631f", label: "Temporary permission" },
  no_permission: { color: "#a4372a", label: "No permission yet" },
};

function expiryCaption(iso) {
  if (!iso) return null;
  const hoursLeft = (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60);
  if (hoursLeft <= 0) return null;
  if (hoursLeft < 48) return `Expires in ${Math.max(1, Math.round(hoursLeft))}h`;
  return `Expires in ${Math.round(hoursLeft / 24)}d`;
}

/**
 * The single entry point for placing a WhatsApp call. Always opens a popover
 * first (matching WhatsApp's own native call-permission UI) rather than
 * dialing immediately — an agent needs to see whether they can actually call
 * before attempting to, since Meta requires a granted permission per contact.
 */
export default function CallButton({ contactInfo }) {
  const active = useSelector((state) => state.calls.active);
  const incoming = useSelector((state) => state.calls.incoming);
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const userPermissions = useSelector((state) => state.userDetails.userPermissions);
  const subscriptionFeatures = useSelector((state) => state.subscription?.features);
  const [anchorEl, setAnchorEl] = useState(null);
  const contactId = contactInfo?._id;
  // Hooks can't sit behind the platform gate below, so skip the fetch here instead — QR/Instagram/etc.
  // contacts render nothing and have no Meta call permission to look up (it would only 409).
  const { data, loading, error, errorCode, requesting, requestPermission } = useCallPermission(
    contactInfo?.platform === "whatsapp" ? contactId : null
  );

  // Same fail-open-while-loading pattern used for the AI/Calls nav gates —
  // Outbound (Calls.Delete) is what the backend's roleauth("Calls.Delete")
  // on POST /dial actually enforces; hide the entry point rather than let it
  // open a popover that can only ever 403 on dial.
  const outboundAllowed = userPermissions == null || userPermissions?.permissions?.Calls?.Delete === true;
  if (!outboundAllowed) return null;
  // Same fail-open pattern and same enabled-AND-limit check LeftNav.jsx's "Calls" nav gate uses
  // (see its own comment for why `enabled` alone isn't enough — a per-customer override can only
  // ever zero the numeric limit, never flip `enabled` itself) — a plan/account with calling
  // switched off, or with both directions zeroed out for this specific account, must not offer
  // this button anywhere it's rendered (chat header, InboxAll, CallsSection, CallsSettings all
  // share this one component).
  const callMinutesInboundFeature = subscriptionFeatures?.find((f) => f.key === "callMinutesInbound");
  const callMinutesOutboundFeature = subscriptionFeatures?.find((f) => f.key === "callMinutesOutbound");
  const callingFeatureEnabled =
    subscriptionFeatures == null ||
    subscriptionFeatures.length === 0 ||
    (callMinutesOutboundFeature?.enabled !== false &&
      (Number(callMinutesInboundFeature?.limit || 0) > 0 || Number(callMinutesOutboundFeature?.limit || 0) > 0));
  if (!callingFeatureEnabled) return null;
  if (contactInfo?.platform !== "whatsapp" || !contactInfo?.phoneNumberID) return null;
  // Calling is a permanent, by-design QR limitation (Baileys can only detect/reject call
  // signaling, never carry audio/video — see the plan's non-goals). This is the single shared
  // entry point (chat header, InboxAll, CallsSection, CallsSettings), so gating it here hides
  // the button everywhere at once — matches LeftNav.jsx's callsNavAllowed/InboxAll.jsx's
  // callsViewAllowed, same reasoning.
  if (currentWorkspace?.whatsappConnectionType === "baileys") return null;

  const busy = Boolean(active || incoming);
  const open = Boolean(anchorEl);
  const meta = STATUS_META[data?.status] || STATUS_META.no_permission;
  const expiry = data?.status === "temporary" ? expiryCaption(data.expiresAt) : null;
  const callDisabled = busy || loading || data?.canStartCall === false;

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleCall = () => {
    if (callDisabled) return;
    connectionMonitor.emit("call:requestDial", {
      to: contactInfo.phoneNumberID,
      contactId: contactInfo._id,
      contactName: contactInfo.name || null,
    });
    handleClose();
  };

  const handleRequest = async () => {
    const result = await requestPermission();
    if (!result.success) toast.error(result.error || "Couldn't send the permission request — try again.");
  };

  return (
    <>
      <IconButtonWithTooltip label="Call" onClick={handleOpen} size="small">
        <Badge
          variant="dot"
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ "& .MuiBadge-dot": { bgcolor: loading ? "#bdbdbd" : meta.color, width: 8, height: 8, borderRadius: "50%" } }}
        >
          <Box sx={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", color: "text.primary" }}>
            <CallIconIx size="100%" color="currentColor" variant="Linear" />
          </Box>
        </Badge>
      </IconButtonWithTooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // Anchored to a button that sits inside the top nav itself — the
        // default anchor math left it overlapping the nav bar instead of
        // clearing it (Rahim, 2026-08-31: "fix this to be under the top
        // nav"). Same "must sit below the nav" requirement the toast
        // redesign already solved, applied here too — a small explicit mt
        // plus a zIndex clearly above the nav's own stacking, rather than
        // trusting the anchor's raw computed position.
        sx={{ mt: 1, zIndex: 1400 }}
        PaperProps={{ sx: { width: 340, borderRadius: 3, boxShadow: "0 12px 32px rgba(0,0,0,0.18)" } }}
      >
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.875rem", lineHeight: 1.4 }}>
              WhatsApp call via <b>{currentWorkspace?.name || "your business"}</b>
            </Typography>
            <BetaTag />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ px: 2.5, py: 1.75 }}>
          {error ? (
            errorCode === "CALLING_NOT_CONFIGURED" ? (
              // A distinct, calmer treatment from the generic red error below —
              // this isn't "you don't have permission for this contact" (a
              // normal, per-contact state), it's the whole calling integration
              // being broken account-wide, which needs an admin, not a retry.
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 18, color: "#b8631f", mt: "1px", flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}>Calling unavailable</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.25 }}>{error}</Typography>
                </Box>
              </Box>
            ) : (
              <Typography sx={{ fontSize: "0.8125rem", color: "#a4372a" }}>{error}</Typography>
            )
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: loading ? "#bdbdbd" : meta.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                  {loading ? "Checking permission…" : meta.label}
                  {expiry ? ` · ${expiry}` : ""}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Box
                  onClick={handleCall}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: callDisabled ? "default" : "pointer",
                    opacity: callDisabled ? 0.45 : 1,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", flexShrink: 0 }}>
                    <CallIconIx size="100%" color="currentColor" variant="Linear" />
                  </Box>
                  <Typography sx={{ fontSize: "0.9375rem", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Call {contactInfo.phoneNumberID}
                  </Typography>
                </Box>

                {data?.status !== "permanent" && !loading && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    {requesting ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Typography
                        onClick={data?.canRequestPermission === false ? undefined : handleRequest}
                        sx={{
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          color: data?.canRequestPermission === false ? "text.disabled" : "primary.main",
                          cursor: data?.canRequestPermission === false ? "default" : "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Request permission
                      </Typography>
                    )}
                    <Tooltip
                      title={
                        data?.canRequestPermission === false
                          ? "Request limit reached for now — try again later."
                          : "Sends a WhatsApp message asking this contact to allow calls from you."
                      }
                      componentsProps={DARK_BUBBLE_TOOLTIP_SX}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {busy && (
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 1 }}>Already on a call.</Typography>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
